import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection, buildSectionSelector } from '@theme/section-renderer';

/**
 * @typedef {object} CartInvoiceRefs
 * @property {HTMLElement} cartInvoiceError
 * @property {HTMLElement} cartInvoiceErrorMessage
 * @property {HTMLElement} cartInvoiceSuccess
 * @property {HTMLElement} cartInvoiceMessages
 * @property {HTMLButtonElement} vatSubmitButton
 */
/**
 * @extends {Component<CartInvoiceRefs>}
 */
class CartInvoiceComponent extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;
  /** @type {Record<string, string> | null} */
  #messages = null;

  connectedCallback() {
    super.connectedCallback();
    this.#loadMessages();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * @param {Event} event
   */
  validateVat = async (event) => {
    event.preventDefault();
    if (!(event.target instanceof HTMLFormElement)) return;

    const form = event.target;
    const input = form.querySelector('input[name="vat"]');
    const emailInput = form.querySelector('input[name="invoice_email"]');
    const hasEmailField = emailInput instanceof HTMLInputElement;
    if (!(input instanceof HTMLInputElement)) return;

    this.#loadMessages();

    const vat = input.value.trim()?.replace(/\s+/g, '').toUpperCase();
    const email = hasEmailField ? emailInput.value.trim() : '';

    if (!vat) {
      this.#toggleMessage(
        'error',
        this.#getMessage('empty', 'Please enter a VAT number.'),
      );
      return;
    }

    if (hasEmailField) {
      if (!email) {
        this.#toggleMessage(
          'error',
          this.#getMessage('emailRequired', 'Email address is required.'),
        );
        return;
      }

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(email)) {
        this.#toggleMessage(
          'error',
          this.#getMessage('emailInvalid', 'Please enter a valid email address.'),
        );
        return;
      }
    }

    this.#setVatAsyncState(form, true);

    try {
      const { valid, errorMessage } = await this.#validateVatNumber(vat, email);
      if (valid) {
        await this.#updateCart(vat, email);
        this.#toggleMessage('success');
      } else {
        await this.#clearCartKeepingInput();
        this.#toggleMessage('error', errorMessage || 'Invalid VAT number or email');
      }
    } catch (error) {
      const fallback = error instanceof Error && error.message ? error.message : 'Validation failed';
      await this.#clearCartKeepingInput();
      this.#toggleMessage('error', this.#getMessage('unexpected', fallback));
    } finally {
      this.#setVatAsyncState(form, false);
    }
  };

  /**
   * Clears the VAT attributes and keeps what the customer typed: the section morph in `#clearCart`
   * resets the inputs (and the error message) to the server state. Show the error after this call.
   */
  async #clearCartKeepingInput() {
    const restoreInputs = keepInvoiceInputs(this);
    try {
      await this.#clearCart();
    } catch {
      // Still show the error. If the old VAT attribute stays, cart-checkout-gate.js keeps the VAT item
      // open because the field no longer matches the validated number.
    }
    restoreInputs();
  }

  /**
   * @param {string} vatNumber
   * @param {string} email
   * @returns {Promise<{ valid: boolean; errorMessage?: string }>}
   */
  #validateVatNumber = async (vatNumber, email) => {
    const controller = this.#createAbortController();

    try {
      const baseEndpoint = Theme.routes.vat_validation_url;
      const mode = this.dataset.vatMode || 'vat_and_email';
      const url = new URL(baseEndpoint, window.location.origin);
      url.searchParams.set('vat_number', vatNumber);
      if (mode === 'vat_and_email') {
        url.searchParams.set('email', email);
      }

      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        signal: controller.signal,
      });

      let data;
      try {
        data = await res.json();
      } catch {
        data = undefined;
      }

      if (!res.ok) {
        const message = await this.#getApiErrorMessage(res, data);
        return { valid: false, errorMessage: message };
      }

      if (data?.valid === true || data?.isValid === true) {
        return { valid: true };
      }

      const apiError = typeof data?.error === 'string' && data.error ? data.error : '';
      const message =
        apiError ||
        this.#getMessage(
          'invalidFormat',
          'The VAT number could not be validated. Please check it and try again.',
        );

      return { valid: false, errorMessage: message };
    } catch {
      return {
        valid: false,
        errorMessage: this.#getMessage(
          'network',
          'We could not reach the VAT validation service. Please try again.',
        ),
      };
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * @param {Response} response
   * @param {any} data
   * @returns {Promise<string>}
   */
  async #getApiErrorMessage(response, data) {
    let statusMessage;

    if (response.status === 429) {
      statusMessage = this.#getMessage(
        'rateLimit',
        'Too many validation attempts. Please wait a moment and try again.',
      );
    } else if (response.status === 422 || response.status === 400) {
      statusMessage = this.#getMessage(
        'invalidFormat',
        'The VAT number format appears invalid. Please check it and try again.',
      );
    } else if (response.status === 404) {
      statusMessage = this.#getMessage(
        'serviceUnavailable',
        'The VAT validation service is temporarily unavailable. Please try again later.',
      );
    } else {
      statusMessage = this.#getMessage(
        'unexpected',
        'An unexpected error occurred while validating your VAT number.',
      );
    }

    const payload = data;
    if (payload && typeof payload.error === 'string' && payload.error) {
      const apiError = payload.error;

      if (apiError.includes('MS_MAX_CONCURRENT_REQ')) {
        return this.#getMessage(
          'maxConcurrent',
          'The VAT validation service is currently handling too many requests. Please wait a moment and try again.',
        );
      }

      return `${statusMessage} (${apiError})`;
    }

    return statusMessage;
  }

  #loadMessages() {
    if (this.#messages) return;

    /** @type {HTMLScriptElement | null} */
    const script = this.querySelector('[ref="cartInvoiceMessages"]');

    this.#messages = {};

    if (!script || !script.textContent) return;

    try {
      const parsed = JSON.parse(script.textContent);
      if (parsed && typeof parsed === 'object') {
        this.#messages = /** @type {Record<string, string>} */ (parsed);
      }
    } catch {
      // Ignore JSON parse errors – fall back to hardcoded messages
    }
  }

  /**
   * @param {string} key
   * @param {string} fallback
   */
  #getMessage(key, fallback) {
    const messages = this.#messages;
    if (!messages || typeof messages !== 'object') return fallback;

    const value = Object.prototype.hasOwnProperty.call(messages, key)
      ? messages[key]
      : undefined;

    return typeof value === 'string' && value ? value : fallback;
  }

  /**
   * Section IDs for every `cart-items-component` on the page (drawer + cart page, etc.),
   * matching `component-cart-items.js` / `product-form.js` so `/cart/update` returns HTML.
   * @returns {string[]}
   */
  #getCartSectionIds() {
    const ids = new Set();
    const own = this.sectionId;
    if (own) ids.add(own);

    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.add(el.dataset.sectionId);
      }
    });

    return Array.from(ids);
  }

  /**
   * @param {Record<string, string> | undefined} sections
   */
  async #morphCartSections(sections) {
    if (!sections || typeof sections !== 'object') return;

    for (const [sectionId, html] of Object.entries(sections)) {
      if (typeof html !== 'string' || !html) continue;

      const existingElement = document.getElementById(buildSectionSelector(sectionId));
      if (!existingElement) continue;

      const cartItems = existingElement.querySelector('cart-items-component');
      const isDrawer =
        cartItems instanceof HTMLElement && cartItems.dataset.drawer !== undefined;

      await morphSection(sectionId, html, isDrawer ? 'hydration' : 'full');
    }
  }

  /**
   * @param {string} vatNumber
   * @param {string} email
   */
  #updateCart = async (vatNumber, email) => {
    localStorage.setItem('invoice-requested-status', 'yes');
    localStorage.setItem('invoice-vat-value', vatNumber);
    localStorage.setItem('invoice-email-value', email);

    const config = fetchConfig('json', {
      body: JSON.stringify({
        attributes: { 'Invoice requested': 'yes', 'VAT Number': vatNumber, 'Invoice email': email },
        sections: this.#getCartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });

    const data = await fetch(Theme.routes.cart_update_url, config).then((r) => r.json());
    await this.#morphCartSections(data.sections);
  };

  #clearCart = async () => {
    localStorage.setItem('invoice-requested-status', 'no');
    localStorage.setItem('invoice-vat-value', '');
    localStorage.setItem('invoice-email-value', '');

    const config = fetchConfig('json', {
      body: JSON.stringify({
        attributes: { 'Invoice requested': '', 'VAT Number': '', 'Invoice email': '' },
        sections: this.#getCartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });

    const data = await fetch(Theme.routes.cart_update_url, config).then((r) => r.json());
    await this.#morphCartSections(data.sections);
  };

  /**
   * @param {'error' | 'success'} type
   * @param {string} message
   */
  #toggleMessage(type, message = '') {
    const error = /** @type {HTMLElement | undefined} */ (this.refs.cartInvoiceError);
    const errorMsg = /** @type {HTMLElement | undefined} */ (this.refs.cartInvoiceErrorMessage);
    const success = /** @type {HTMLElement | undefined} */ (this.refs.cartInvoiceSuccess);

    error?.classList.toggle('hidden', type === 'success');
    errorMsg?.classList.toggle('hidden', type === 'success');
    success?.classList.toggle('hidden', type === 'error');

    if (type === 'error' && errorMsg) errorMsg.textContent = message;
  }

  /**
   * @param {HTMLFormElement} form
   * @param {boolean} disabled
   */
  #setFormDisabled(form, disabled) {
    form.querySelectorAll('input, button').forEach(/** @param {Element} el */(el) => el.toggleAttribute('disabled', disabled));
  }

  /**
   * Disables the VAT form and shows submit loading state while validating/updating the cart.
   * @param {HTMLFormElement} form
   * @param {boolean} busy
   */
  #setVatAsyncState(form, busy) {
    this.#setFormDisabled(form, busy);

    const submit = this.refs.vatSubmitButton;
    if (!(submit instanceof HTMLButtonElement)) return;

    submit.classList.toggle('cart-invoice__button--loading', busy);

    if (busy) {
      submit.setAttribute('aria-busy', 'true');
      const validatingLabel = this.dataset.invoiceValidatingLabel;
      if (validatingLabel) submit.setAttribute('aria-label', validatingLabel);
    } else {
      submit.removeAttribute('aria-busy');
      submit.removeAttribute('aria-label');
    }
  }

  get sectionId() {
    return this.dataset.sectionId || '';
  }
}

if (!customElements.get('cart-invoice-component')) {
  customElements.define('cart-invoice-component', CartInvoiceComponent);
}

/**
 * Handles the Terms and Conditions checkbox change.
 * Updates cart attribute and morphs sections so the checkout gate re-evaluates.
 * @param {HTMLInputElement} checkbox
 */
async function handleTermsCheckboxChange(checkbox) {
  const checked = checkbox.checked;
  const invoiceComponent = checkbox.closest('cart-items-component')?.querySelector('cart-invoice-component');
  if (!(invoiceComponent instanceof HTMLElement)) return;

  /** @type {any} cart-checkout-gate.js: shows the loading state and holds checkout clicks during the save. */
  const gate = checkbox.closest('cart-checkout-gate');
  const sectionIds = getSectionIds(invoiceComponent);

  const config = fetchConfig('json', {
    body: JSON.stringify({
      attributes: { 'Terms and Conditions': checked ? 'true' : '' },
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  });

  const save = (async () => {
    // A VAT number or e-mail typed but not validated yet would otherwise be wiped by the morph.
    const restoreInputs = keepInvoiceInputs(invoiceComponent);
    try {
      const data = await fetch(Theme.routes.cart_update_url, config).then((r) => r.json());
      await morphCartSections(data.sections);
      restoreInputs();
    } catch {
      checkbox.checked = !checked;
      // Untick the live checklist item again.
      gate?.syncTerms?.();
    }
  })();

  gate?.trackSave?.(save);
  await save;
}

/**
 * Remembers the VAT and e-mail values typed in the invoice form, so they can be put back after a
 * section morph resets them to the saved cart attributes. Also re-syncs the checkout checklist.
 * @param {Element} invoiceComponent
 * @returns {() => void} Puts the values back.
 */
function keepInvoiceInputs(invoiceComponent) {
  const fields = ['vat', 'invoice_email'].map((name) => {
    const input = invoiceComponent.querySelector(`input[name="${name}"]`);
    return { name, value: input instanceof HTMLInputElement ? input.value : null };
  });

  return () => {
    for (const { name, value } of fields) {
      const input = invoiceComponent.querySelector(`input[name="${name}"]`);
      if (input instanceof HTMLInputElement && value !== null) input.value = value;
    }

    /** @type {any} */
    const gate = invoiceComponent.closest('cart-items-component')?.querySelector('cart-checkout-gate');
    gate?.syncVat?.();
  };
}

/**
 * Collects section IDs from all cart-items-component elements and the invoice component.
 * @param {HTMLElement} invoiceComponent
 * @returns {string[]}
 */
function getSectionIds(invoiceComponent) {
  const ids = new Set();
  const own = invoiceComponent.dataset?.sectionId;
  if (own) ids.add(own);

  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.add(el.dataset.sectionId);
    }
  });

  return Array.from(ids);
}

/**
 * Morphs cart sections from returned HTML (shared with CartInvoiceComponent).
 * @param {Record<string, string> | undefined} sections
 */
async function morphCartSections(sections) {
  if (!sections || typeof sections !== 'object') return;

  for (const [sectionId, html] of Object.entries(sections)) {
    if (typeof html !== 'string' || !html) continue;

    const existingElement = document.getElementById(buildSectionSelector(sectionId));
    if (!existingElement) continue;

    const cartItems = existingElement.querySelector('cart-items-component');
    const isDrawer = cartItems instanceof HTMLElement && cartItems.dataset.drawer !== undefined;

    await morphSection(sectionId, html, isDrawer ? 'hydration' : 'full');
  }
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.hasAttribute('data-cart-terms-checkbox')) return;

  handleTermsCheckboxChange(target);
});
